import { helpArticleService } from './helpArticleService';
import { HelpArticle } from '../types/helpArticle';
import { callAIProxy, aiProxyBaseUrl } from './aiService';

interface AIHelpResponse {
  answer: string;
  relatedArticles: HelpArticle[];
  confidence: 'high' | 'medium' | 'low';
}

class HelpAIService {
  // SECURITY: routed through the aiServiceProxy Cloud Function (no API key in the bundle).
  private async makeRequest(endpoint: string, data: any): Promise<any> {
    return callAIProxy(aiProxyBaseUrl(), endpoint, data);
  }

  async answerQuestion(question: string, context?: string): Promise<AIHelpResponse> {
    try {
      // First, search for relevant articles
      const searchResults = await helpArticleService.searchArticles(question);
      const relevantArticles = searchResults.slice(0, 5);

      // Build context from articles
      const articlesContext = relevantArticles
        .map((article, index) => {
          return `Article ${index + 1}: ${article.title}\nContent: ${article.content.substring(0, 500)}...`;
        })
        .join('\n\n');

      // Create prompt for AI
      const prompt = `You are a helpful support assistant for Scubamail, an email marketing platform. Answer the user's question based on the provided help articles and your knowledge of email marketing.

${context ? `Additional Context: ${context}\n\n` : ''}
Available Help Articles:
${articlesContext || 'No specific articles found, but use your knowledge of email marketing and Scubamail features.'}

User Question: ${question}

Instructions:
1. Provide a clear, concise, and helpful answer
2. If relevant articles are available, reference them naturally
3. If the question is about Scubamail features, be specific
4. If you don't have enough information, suggest the user contact support
5. Keep the answer conversational and friendly
6. Format your response in plain text (no markdown)

Answer:`;

      // Call Gemini API
      const response = await this.makeRequest('/models/gemini-2.0-flash:generateContent', {
        contents: [{
          parts: [{
            text: prompt
          }]
        }],
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 1000,
        }
      });

      const answer = response.candidates?.[0]?.content?.parts?.[0]?.text || 
        'I apologize, but I couldn\'t generate an answer at this time. Please try rephrasing your question or contact our support team.';

      // Determine confidence based on article matches
      let confidence: 'high' | 'medium' | 'low' = 'low';
      if (relevantArticles.length >= 3) {
        confidence = 'high';
      } else if (relevantArticles.length >= 1) {
        confidence = 'medium';
      }

      return {
        answer: answer.trim(),
        relatedArticles: relevantArticles,
        confidence,
      };
    } catch (error) {
      console.error('Error getting AI answer:', error);
      
      // Fallback: return search results
      const searchResults = await helpArticleService.searchArticles(question);
      
      return {
        answer: error instanceof Error && error.message.includes('not configured')
          ? 'AI features are not currently configured. Here are some relevant articles that might help:'
          : 'I encountered an error while processing your question. Here are some relevant articles that might help:',
        relatedArticles: searchResults.slice(0, 5),
        confidence: searchResults.length > 0 ? 'medium' : 'low',
      };
    }
  }

  isConfigured(): boolean {
    return true; // configured server-side (aiServiceProxy)
  }
}

export const helpAIService = new HelpAIService();

